import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Brain, LogOut, ArrowRight, Loader2, CreditCard, Key, Trash2, Plug, Globe, Palette } from "lucide-react";

// Curated list of common IANA timezones (covers >99% of active users). Users
// with rarer zones can hit the API directly with a valid IANA name.
const TIMEZONE_OPTIONS = [
  "UTC",
  "Europe/Amsterdam", "Europe/Berlin", "Europe/Madrid", "Europe/Paris",
  "Europe/Rome", "Europe/London", "Europe/Lisbon", "Europe/Athens",
  "Europe/Warsaw", "Europe/Istanbul",
  "Africa/Cairo", "Africa/Johannesburg", "Africa/Lagos",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Bangkok", "Asia/Singapore",
  "Asia/Tokyo", "Asia/Seoul", "Asia/Shanghai", "Asia/Jakarta",
  "Australia/Sydney", "Australia/Perth", "Pacific/Auckland",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Phoenix", "America/Anchorage", "Pacific/Honolulu",
  "America/Toronto", "America/Vancouver", "America/Mexico_City",
  "America/Bogota", "America/Sao_Paulo", "America/Argentina/Buenos_Aires",
];

export default function Settings() {
  const { user, logout } = useAuth();
  const { sub } = useSubscription();
  const nav = useNavigate();
  const [prefs, setPrefs] = useState({
    email_product_updates: true,
    email_weekly_digest: true,
    email_trial_reminders: true,
    email_promo: false,
  });
  const [dailyReminderEnabled, setDailyReminderEnabled] = useState(true);
  const [reminderLoading, setReminderLoading] = useState(true);
  const [timezone, setTimezone] = useState("UTC");
  const [tzLoading, setTzLoading] = useState(true);
  const [waEnabled, setWaEnabled] = useState(false);
  const [waPhone, setWaPhone] = useState("");
  const [waLoading, setWaLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/preferences")
      .then(({ data }) => setPrefs((p) => ({ ...p, ...(data.preferences || {}) })))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get("/strategy/reminders/preferences")
      .then(({ data }) => setDailyReminderEnabled(data.enabled))
      .catch(() => {})
      .finally(() => setReminderLoading(false));
    api.get("/me/timezone")
      .then(({ data }) => setTimezone(data.timezone || "UTC"))
      .catch(() => {})
      .finally(() => setTzLoading(false));
    // Sprint 5.6: WhatsApp opt-in state.
    api.get("/me/whatsapp")
      .then(({ data }) => { setWaEnabled(!!data.enabled); setWaPhone(data.phone_number || ""); })
      .catch(() => {})
      .finally(() => setWaLoading(false));
  }, []);

  const toggle = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));

  const toggleDailyReminder = async () => {
    const next = !dailyReminderEnabled;
    setDailyReminderEnabled(next); // optimistic
    try {
      await api.patch("/strategy/reminders/preferences", { enabled: next });
      toast.success(next ? "Daily reminders on" : "Daily reminders off");
    } catch {
      setDailyReminderEnabled(!next);
      toast.error("Could not update reminder preference");
    }
  };

  const saveTimezone = async (next) => {
    const prev = timezone;
    setTimezone(next); // optimistic
    try {
      const { data } = await api.patch("/me/timezone", { timezone: next });
      setTimezone(data.timezone);
      toast.success(`Timezone set to ${data.timezone}`);
    } catch (err) {
      setTimezone(prev);
      toast.error(err?.response?.data?.detail || "Invalid timezone");
    }
  };

  const saveWhatsApp = async (next) => {
    try {
      const { data } = await api.patch("/me/whatsapp", { enabled: next.enabled, phone_number: next.phone_number });
      setWaEnabled(!!data.enabled);
      setWaPhone(data.phone_number || "");
      toast.success(data.enabled ? "WhatsApp reminders on" : "WhatsApp reminders off");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save WhatsApp preferences");
    }
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/me/preferences", { preferences: prefs }); toast.success("Preferences saved"); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const requestDelete = async () => {
    const confirm1 = window.prompt('This will PERMANENTLY delete your account, Dive Brain, and all Vault items.\n\nType your email to confirm:');
    if (confirm1 !== user?.email) { if (confirm1 !== null) toast.error("Email did not match. Cancelled."); return; }
    try {
      await api.delete("/me");
      toast.success("Account deleted. Signing you out.");
      await logout();
      nav("/", { replace: true });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete account"); }
  };

  return (
    <div data-testid="settings-page" className="px-5 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-3xl">
      <div className="dms-label mb-3">Preferences</div>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-[#0A2342]">Settings</h1>
      <p className="mt-2 text-[#475569] text-[14.5px]">Notifications, quick links and account controls.</p>

      {/* Email preferences */}
      <div className="mt-8 dms-card p-5 sm:p-6" data-testid="settings-email-prefs">
        <div className="flex items-center gap-3 mb-4">
          <Bell className="w-5 h-5 text-[#1D4E89]" />
          <div className="font-heading font-bold text-[#0A2342]">Email preferences</div>
        </div>
        {/* Sprint 4: Daily Strategy-Operator reminder toggle — separate from the marketing prefs. */}
        <label data-testid="pref-daily-reminder" className="flex items-start gap-3 py-2 cursor-pointer border-b border-[#F1F5F9] mb-3 pb-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={dailyReminderEnabled}
            disabled={reminderLoading}
            onChange={toggleDailyReminder}
            data-testid="pref-daily-reminder-input"
          />
          <div>
            <div className="text-[14.5px] font-semibold text-[#0A2342]">
              Daily strategy reminders {reminderLoading && <Loader2 className="w-3 h-3 animate-spin inline ml-1" />}
            </div>
            <div className="text-[13px] text-[#64748B]">
              A short morning email with today&apos;s hook, angle and CTA plus a one-click link to generate the post. Recommended if you use the 90-day calendar.
            </div>
          </div>
        </label>

        {/* Sprint 5.5: per-user timezone. Affects when the daily reminder and Sunday consistency-report emails land. */}
        <div data-testid="settings-timezone" className="flex items-start gap-3 py-2 border-b border-[#F1F5F9] mb-3 pb-3">
          <Globe className="w-4 h-4 mt-1 text-[#1D4E89]" />
          <div className="flex-1 min-w-0">
            <div className="text-[14.5px] font-semibold text-[#0A2342] flex items-center gap-2">
              Your timezone {tzLoading && <Loader2 className="w-3 h-3 animate-spin" />}
            </div>
            <div className="text-[13px] text-[#64748B] mb-2">
              Sets when daily reminders (your local ~7 am) and the Sunday consistency report (your local ~8 am Sunday) arrive.
            </div>
            <select
              value={timezone}
              onChange={(e) => saveTimezone(e.target.value)}
              disabled={tzLoading}
              data-testid="settings-timezone-select"
              className="dms-input text-[13px] max-w-xs"
            >
              {!TIMEZONE_OPTIONS.includes(timezone) && (
                <option value={timezone}>{timezone} (custom)</option>
              )}
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
        </div>
        {/* Sprint 5.6: WhatsApp opt-in — phone number + enable switch. */}
        <div data-testid="settings-whatsapp" className="flex items-start gap-3 py-2 border-b border-[#F1F5F9] mb-3 pb-3">
          <div className="w-4 h-4 mt-1 text-[#25D366] font-bold">W</div>
          <div className="flex-1 min-w-0">
            <div className="text-[14.5px] font-semibold text-[#0A2342] flex items-center gap-2">
              WhatsApp reminders {waLoading && <Loader2 className="w-3 h-3 animate-spin" />}
            </div>
            <div className="text-[13px] text-[#64748B] mb-2">
              Get today&apos;s hook on WhatsApp in addition to email. Reply <b>STOP</b> at any time to unsubscribe.
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="tel"
                inputMode="numeric"
                value={waPhone}
                onChange={(e) => setWaPhone(e.target.value)}
                placeholder="491234567890 (digits only, incl. country code)"
                data-testid="whatsapp-phone-input"
                className="dms-input text-[13px] max-w-xs"
              />
              <button
                onClick={() => saveWhatsApp({ enabled: !waEnabled, phone_number: waPhone })}
                disabled={waLoading || (!waEnabled && !waPhone.trim())}
                data-testid="whatsapp-toggle-btn"
                className={
                  waEnabled
                    ? "rounded-md bg-emerald-600 text-white px-4 py-2 text-[12.5px] font-semibold hover:bg-emerald-700 disabled:opacity-60"
                    : "rounded-md bg-[#0A2342] text-white px-4 py-2 text-[12.5px] font-semibold hover:bg-[#152F5A] disabled:opacity-60"
                }
              >
                {waEnabled ? "On · click to disable" : "Enable WhatsApp"}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-[#64748B] text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <div className="space-y-3">
            {[
              { k: "email_product_updates", t: "Product updates", d: "New tools, feature releases and major DMS improvements." },
              { k: "email_weekly_digest", t: "Weekly Dive Brain digest", d: "A short weekly summary of what you generated + one strategic nudge." },
              { k: "email_trial_reminders", t: "Trial and billing reminders", d: "We'll remind you a couple of days before your trial ends. Recommended." },
              { k: "email_promo", t: "Occasional offers", d: "Occasional discount codes, launches and diver-specific promos. Off by default." },
            ].map((opt) => (
              <label key={opt.k} data-testid={`pref-${opt.k}`} className="flex items-start gap-3 py-2 cursor-pointer">
                <input type="checkbox" className="mt-1" checked={!!prefs[opt.k]} onChange={() => toggle(opt.k)} />
                <div>
                  <div className="text-[14.5px] font-semibold text-[#0A2342]">{opt.t}</div>
                  <div className="text-[13px] text-[#64748B]">{opt.d}</div>
                </div>
              </label>
            ))}
            <div className="mt-4 flex justify-end">
              <button onClick={save} disabled={saving} data-testid="settings-save-prefs" className="dms-btn-primary">{saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : "Save preferences"}</button>
            </div>
          </div>
        )}
      </div>

      {/* Quick links */}
      <div className="mt-4 space-y-3">
        <Link to="/dive-brain" className="dms-card dms-card-hover p-5 flex items-center gap-4" data-testid="settings-brain-link">
          <div className="w-10 h-10 rounded-md bg-[#4FD1C5]/15 text-[#1D4E89] flex items-center justify-center"><Brain className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#0A2342]">Edit Dive Brain</div>
            <div className="text-[13px] text-[#64748B]">Update niche, audience, tone and content pillars.</div>
          </div>
          <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
        </Link>

        <Link to="/billing" className="dms-card dms-card-hover p-5 flex items-center gap-4" data-testid="settings-billing-link">
          <div className="w-10 h-10 rounded-md bg-[#4FD1C5]/15 text-[#1D4E89] flex items-center justify-center"><CreditCard className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#0A2342]">Plan & Billing</div>
            <div className="text-[13px] text-[#64748B]">
              {sub?.status === "trialing" ? `Trialing · ${sub.trial_days_left}d left` : sub?.status === "active" ? `Active · ${sub.tier}` : "No active plan"}
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
        </Link>

        <Link to="/integrations" className="dms-card dms-card-hover p-5 flex items-center gap-4" data-testid="settings-integrations-link">
          <div className="w-10 h-10 rounded-md bg-[#4FD1C5]/15 text-[#1D4E89] flex items-center justify-center"><Plug className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#0A2342]">Integrations</div>
            <div className="text-[13px] text-[#64748B]">Connect Notion, Google Drive and more.</div>
          </div>
          <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
        </Link>

        <Link to="/settings/brand-kit" className="dms-card dms-card-hover p-5 flex items-center gap-4" data-testid="settings-brand-kit-link">
          <div className="w-10 h-10 rounded-md bg-[#4FD1C5]/15 text-[#1D4E89] flex items-center justify-center"><Palette className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#0A2342]">Brand Kit</div>
            <div className="text-[13px] text-[#64748B]">Colours, fonts and logo used across your slide exports.</div>
          </div>
          <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
        </Link>

        {user?.auth_provider !== "google" && (
          <button
            data-testid="settings-password-link"
            onClick={() => toast.info("Password change coming soon. Contact support to change your password.")}
            className="w-full text-left dms-card dms-card-hover p-5 flex items-center gap-4"
          >
            <div className="w-10 h-10 rounded-md bg-[#4FD1C5]/15 text-[#1D4E89] flex items-center justify-center"><Key className="w-5 h-5" /></div>
            <div className="flex-1">
              <div className="font-heading font-bold text-[#0A2342]">Change password</div>
              <div className="text-[13px] text-[#64748B]">Update your sign-in password.</div>
            </div>
            <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
          </button>
        )}

        <button
          data-testid="settings-logout"
          onClick={async () => { await logout(); nav("/"); }}
          className="w-full text-left dms-card dms-card-hover p-5 flex items-center gap-4"
        >
          <div className="w-10 h-10 rounded-md bg-[#0A2342]/5 text-[#0A2342] flex items-center justify-center"><LogOut className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#0A2342]">Sign out</div>
            <div className="text-[13px] text-[#64748B]">End your current session.</div>
          </div>
        </button>
      </div>

      {/* Danger zone */}
      <div className="mt-8 dms-card p-5 sm:p-6 border-[#FCA5A5] bg-[#FEF2F2]">
        <div className="flex items-center gap-3 mb-3">
          <Trash2 className="w-4 h-4 text-[#B91C1C]" />
          <div className="font-heading font-bold text-[#B91C1C]">Delete account</div>
        </div>
        <p className="text-[13.5px] text-[#7F1D1D]">Permanently deletes your account, Dive Brain and Vault. This cannot be undone.</p>
        <button
          onClick={requestDelete}
          data-testid="settings-delete-account"
          className="mt-4 inline-flex items-center gap-2 rounded-md px-4 py-2 border border-[#B91C1C] text-[#B91C1C] hover:bg-[#FEE2E2] text-[13.5px] font-semibold"
        >
          <Trash2 className="w-4 h-4" /> Delete my account
        </button>
      </div>
    </div>
  );
}
